import { useState, useEffect, useCallback } from 'react';
import { getOrganizations } from '../../services/admin';
import {
  sendBroadcastNotification,
  type BroadcastScope,
  type BroadcastNotificationResult,
} from '../../services/notifications';
import type { OrganizationSummary } from '../../types/admin';
import { ConfirmModal } from '../../components/ConfirmModal';
import { AdminPage, AdminPageHeader, AdminPanel } from './AdminPage';
import './NotificationsSection.css';

/**
 * System-admin tool to send an ad-hoc test push notification to all registered
 * devices, specific organizations, or explicit device tokens. Access control is
 * handled at the route level by ProtectedRoute (system_admin only).
 */
export const NotificationsSection = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<BroadcastScope>('all');
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [deviceTokensText, setDeviceTokensText] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BroadcastNotificationResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Load orgs lazily the first time the "organizations" scope is chosen.
  const fetchOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const response = await getOrganizations({ pageNumber: 1, pageSize: 200 });
      setOrganizations(response.items);
    } catch {
      setError('Could not load organizations.');
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (scope === 'organizations' && organizations.length === 0) {
      void fetchOrganizations();
    }
  }, [scope, organizations.length, fetchOrganizations]);

  const parsedTokens = deviceTokensText
    .split('\n')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const canSubmit =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !sending &&
    (scope === 'all' ||
      (scope === 'organizations' && selectedOrgIds.length > 0) ||
      (scope === 'devices' && parsedTokens.length > 0) ||
      (scope === 'user' && userEmail.trim().length > 0));

  const toggleOrg = (id: string) => {
    setSelectedOrgIds((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id],
    );
  };

  const confirmMessage = (): string => {
    if (scope === 'all') {
      return 'Send this notification to EVERY registered device across all organizations?';
    }
    if (scope === 'organizations') {
      const n = selectedOrgIds.length;
      return `Send this notification to every device in ${n} organization${n === 1 ? '' : 's'}?`;
    }
    if (scope === 'user') {
      return `Send this notification to every device registered to ${userEmail.trim()}?`;
    }
    return `Send this notification to ${parsedTokens.length} device token${parsedTokens.length === 1 ? '' : 's'}?`;
  };

  const handleSend = async () => {
    setShowConfirm(false);
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const res = await sendBroadcastNotification({
        title: title.trim(),
        body: body.trim(),
        scope,
        organizationIds: scope === 'organizations' ? selectedOrgIds : undefined,
        deviceTokens: scope === 'devices' ? parsedTokens : undefined,
        userEmail: scope === 'user' ? userEmail.trim() : undefined,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification.');
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminPage className="notif">
      <AdminPageHeader
        title="Send a test notification"
        subtitle="Sends a real push notification to registered devices. Use this to verify delivery."
      />

      <AdminPanel className="notif__panel">
        <div className="notif__form">
          <label className="notif__field">
            <span className="notif__label">Title</span>
            <input
              className="notif__input"
              type="text"
              value={title}
              maxLength={200}
              placeholder="e.g. Test notification"
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>

          <label className="notif__field">
            <span className="notif__label">Message</span>
            <textarea
              className="notif__textarea"
              value={body}
              maxLength={1000}
              rows={3}
              placeholder="What should the notification say?"
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          <fieldset className="notif__field notif__fieldset">
            <legend className="notif__label">Send to</legend>
            <label className="notif__radio">
              <input
                type="radio"
                name="scope"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
              />
              <span>All devices (every organization)</span>
            </label>
            <label className="notif__radio">
              <input
                type="radio"
                name="scope"
                checked={scope === 'organizations'}
                onChange={() => setScope('organizations')}
              />
              <span>Specific organizations</span>
            </label>
            <label className="notif__radio">
              <input
                type="radio"
                name="scope"
                checked={scope === 'user'}
                onChange={() => setScope('user')}
              />
              <span>Specific user (by email)</span>
            </label>
            <label className="notif__radio">
              <input
                type="radio"
                name="scope"
                checked={scope === 'devices'}
                onChange={() => setScope('devices')}
              />
              <span>Specific device tokens</span>
            </label>
          </fieldset>

          {scope === 'organizations' && (
            <div className="notif__field">
              <span className="notif__label">Organizations</span>
              {orgsLoading ? (
                <p className="notif__hint">Loading organizations…</p>
              ) : (
                <div className="notif__org-list">
                  {organizations.map((org) => (
                    <label key={org.id} className="notif__org">
                      <input
                        type="checkbox"
                        checked={selectedOrgIds.includes(org.id)}
                        onChange={() => toggleOrg(org.id)}
                      />
                      <span>{org.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {scope === 'user' && (
            <label className="notif__field">
              <span className="notif__label">User email</span>
              <input
                className="notif__input"
                type="email"
                value={userEmail}
                placeholder="coach@example.com"
                onChange={(e) => setUserEmail(e.target.value)}
              />
              <span className="notif__hint">Sends to every device that user has registered.</span>
            </label>
          )}

          {scope === 'devices' && (
            <label className="notif__field">
              <span className="notif__label">Device tokens</span>
              <textarea
                className="notif__textarea"
                value={deviceTokensText}
                rows={4}
                placeholder="One FCM device token per line"
                onChange={(e) => setDeviceTokensText(e.target.value)}
              />
              <span className="notif__hint">{parsedTokens.length} token(s)</span>
            </label>
          )}

          {error && (
            <div className="admin-error" role="alert">
              {error}
            </div>
          )}

          {result && (
            <div
              className={`notif__banner ${result.failed > 0 ? 'notif__banner--warn' : 'notif__banner--success'}`}
            >
              <div>
                Targeted {result.targeted}, delivered {result.delivered}, failed {result.failed}
                {result.pruned > 0 ? `, pruned ${result.pruned} dead token(s)` : ''}.
              </div>
              {result.errors.length > 0 && (
                <ul className="notif__errors">
                  {result.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="notif__actions">
            <button
              className="notif__button"
              type="button"
              disabled={!canSubmit}
              onClick={() => setShowConfirm(true)}
            >
              {sending ? 'Sending…' : 'Send notification'}
            </button>
          </div>
        </div>
      </AdminPanel>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSend}
        title="Send notification?"
        message={confirmMessage()}
        confirmText="Send"
        cancelText="Cancel"
      />
    </AdminPage>
  );
};
